import DOMPurify from 'dompurify';
// The `/search-kmp` build swaps Choices' default fuzzy (Fuse.js) search for
// plain case-insensitive substring matching. Fuzzy scoring ranks matches by
// approximate similarity rather than "contains", so results for filter lists
// like Country/Location/Department can shuffle non-intuitively as you type
// (e.g. a longer, less relevant option outranking an exact substring match).
// Substring matching is what users expect when filtering a list of exact,
// known names.
import Choices from 'choices.js/search-kmp';
import 'choices.js/public/assets/styles/choices.css';

/**
 * Shared Choices.js config for the filter selects: search enabled to make
 * long lists (e.g. locations) easier to narrow down, sorting left off since
 * the options are already sorted server-side.
 */
const CHOICES_OPTIONS = {
	searchEnabled: true,
	shouldSort: false,
	itemSelectText: '',
};

/**
 * Render job listings grouped by department.
 *
 * @param {HTMLElement} block Block root element.
 * @param {Object} data Jobs grouped by department: { [department]: [{ title, url, location, locations, countries }] }.
 *   `location` is a display string (the single location, or a "N Locations" count); `locations` and
 *   `countries` are the full lists filtering matches against.
 * @param {string} searchText Lowercased search text.
 * @param {string} selectedLocation Selected location filter.
 * @param {string} selectedDepartment Selected department filter.
 * @param {string} selectedCountry Selected country filter.
 */
const renderJobs = (block, data, searchText = '', selectedLocation = '', selectedDepartment = '', selectedCountry = '') => {
	const listingsContainer = block.querySelector('.pro-open-positions-listings');
	const departments = Object.keys(data);

	const matches = (job) => (!selectedLocation || (job.locations || []).includes(selectedLocation))
		&& (!selectedCountry || (job.countries || []).includes(selectedCountry))
		&& (!searchText || job.title.toLowerCase().includes(searchText));

	const visibleDepartments = departments.filter(
		(department) => (!selectedDepartment || department === selectedDepartment)
			&& data[department].some(matches),
	);

	if (visibleDepartments.length === 0) {
		listingsContainer.innerHTML = '<div class="pro-hdr pro-hdr-s pro-color-extreme">No jobs matching the filter or search criteria.</div>';
		return;
	}

	let html = '<ul class="pro-open-positions-list">';

	visibleDepartments.forEach((department) => {
		const filteredJobs = data[department].filter(matches);

		html += `<li>
			<h2 class="pro-hdr pro-hdr-s pro-color-extreme">${DOMPurify.sanitize(department)}</h2>
			<ul>`;

		filteredJobs.forEach((job) => {
			html += `<li>
				<a href="${DOMPurify.sanitize(job.url)}" target="_blank" class="pro-open-positions-link pro-fs-2">
					<div>${DOMPurify.sanitize(job.title)}</div>
					<div>${DOMPurify.sanitize(job.location)}</div>
				</a>
			</li>`;
		});

		html += '</ul></li>';
	});

	html += '</ul>';
	listingsContainer.innerHTML = html;
};

/**
 * Re-render jobs for a block based on its current filter/search state.
 *
 * @param {HTMLElement} block Block root element.
 */
const filterJobs = (block) => {
	const data = JSON.parse(block.dataset.jobs);
	const searchInput = block.querySelector('.pro-open-positions-search');
	// Department, Location, and Country filters are each optional — they can
	// be individually disabled per block instance, so none can be assumed present.
	const locationSelect = block.querySelector('.pro-open-positions-location');
	const departmentSelect = block.querySelector('.pro-open-positions-department');
	const countrySelect = block.querySelector('.pro-open-positions-country');
	const clearContainer = block.querySelector('.pro-open-positions-clear');

	const searchText = searchInput.value.toLowerCase();
	const selectedLocation = locationSelect ? locationSelect.value : '';
	const selectedDepartment = departmentSelect ? departmentSelect.value : '';
	const selectedCountry = countrySelect ? countrySelect.value : '';

	if (clearContainer) {
		const hasActiveFilter = !!(searchText || selectedLocation || selectedDepartment || selectedCountry);
		clearContainer.style.display = hasActiveFilter ? '' : 'none';
	}

	renderJobs(block, data, searchText, selectedLocation, selectedDepartment, selectedCountry);
};

/**
 * Wire up search/filter interactivity for a block. Job data is already
 * server-rendered into the markup (from the careers aggregator plugin's
 * feed) and mirrored into the data-jobs attribute, so no network fetch is
 * needed here.
 *
 * @param {HTMLElement} block Block root element.
 */
const initBlock = (block) => {
	const searchInput = block.querySelector('.pro-open-positions-search');
	const searchButton = block.querySelector('.pro-open-positions-search-btn');
	// Department, Location, and Country are all optional: each can be
	// individually disabled per block instance, and Country additionally
	// only renders when the feed has Country custom field data.
	const locationSelect = block.querySelector('.pro-open-positions-location');
	const departmentSelect = block.querySelector('.pro-open-positions-department');
	const countrySelect = block.querySelector('.pro-open-positions-country');
	const clearButton = block.querySelector('.pro-open-positions-clear-btn');

	if (!searchInput) {
		console.error('Open Positions: expected filter controls not found', block);
		return;
	}

	const locationChoices = locationSelect ? new Choices(locationSelect, CHOICES_OPTIONS) : null;
	const departmentChoices = departmentSelect ? new Choices(departmentSelect, CHOICES_OPTIONS) : null;
	const countryChoices = countrySelect ? new Choices(countrySelect, CHOICES_OPTIONS) : null;

	searchInput.addEventListener('keyup', (e) => {
		if (e.key === 'Enter') {
			filterJobs(block);
		}
	});

	if (searchButton) {
		searchButton.addEventListener('click', () => filterJobs(block));
	}

	if (locationSelect) {
		locationSelect.addEventListener('change', () => filterJobs(block));
	}

	if (departmentSelect) {
		departmentSelect.addEventListener('change', () => filterJobs(block));
	}

	if (countrySelect) {
		countrySelect.addEventListener('change', () => filterJobs(block));
	}

	if (clearButton) {
		clearButton.addEventListener('click', () => {
			searchInput.value = '';

			if (locationChoices) {
				locationChoices.setChoiceByValue('');
			}

			if (departmentChoices) {
				departmentChoices.setChoiceByValue('');
			}

			if (countryChoices) {
				countryChoices.setChoiceByValue('');
			}

			filterJobs(block);
		});
	}
};

/**
 * Initialize open positions blocks.
 */
const initOpenPositions = () => {
	const blocks = document.querySelectorAll('.pro-open-positions');

	blocks.forEach(initBlock);
};

// Run on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initOpenPositions);
} else {
	// DOM is already ready
	initOpenPositions();
}
